/**
 * @file use-responsive.ts
 *
 * Hook that tracks the current viewport dimensions and exposes Gravity
 * design-system breakpoint flags (`isMobile`, `isTablet`, `isDesktop` and their
 * fine-grained variants), plus utility helpers `isBreakpoint`, `isBetween`,
 * `getSpacing`, and `getContainer`. Breakpoint values are aligned with the
 * Gravity CSS framework. Ported from the design-reviewed Website Builder v2
 * implementation.
 */

import { useEffect, useState } from 'react';
import {
  BREAKPOINTS,
  CONTAINERS,
  SPACING,
  type GravityBreakPoint,
  type GravityContainer,
  type GravitySpacing,
} from '../gravity-breakpoints';

type UseResponsiveReturn = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isMobileSmall: boolean;
  isMobileLarge: boolean;
  isTabletSmall: boolean;
  isTabletLarge: boolean;
  isDesktopSmall: boolean;
  isDesktopLarge: boolean;
  width: number;
  height: number;
  isBreakpoint: (
    comparison: 'min' | 'max',
    breakpoint: GravityBreakPoint | number,
  ) => boolean;
  isBetween: (min: GravityBreakPoint, max: GravityBreakPoint) => boolean;
  getSpacing: (size: GravitySpacing) => number;
  getContainer: (size: GravityContainer) => number;
};

/**
 * Tracks viewport size and derives Gravity breakpoint flags and helpers.
 *
 * @returns Breakpoint flags, current width/height, and Gravity sizing helpers.
 */
export function useResponsive(): UseResponsiveReturn {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleResize = (): void => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { width, height } = dimensions;

  const isBreakpoint = (
    comparison: 'min' | 'max',
    breakpoint: GravityBreakPoint | number,
  ): boolean => {
    const value =
      typeof breakpoint === 'number' ? breakpoint : BREAKPOINTS[breakpoint];
    return comparison === 'min' ? width >= value : width <= value;
  };

  const isBetween = (
    min: GravityBreakPoint,
    max: GravityBreakPoint,
  ): boolean => {
    return width >= BREAKPOINTS[min] && width <= BREAKPOINTS[max];
  };

  const isMobile = width <= BREAKPOINTS.mobile_max;
  const isTablet =
    width >= BREAKPOINTS.tablet_min && width <= BREAKPOINTS.tablet_max;
  const isDesktop = width >= BREAKPOINTS.desktop_min;

  const isMobileSmall = width <= BREAKPOINTS.mobile_small_max;
  const isMobileLarge =
    width >= BREAKPOINTS.mobile_large_min && width <= BREAKPOINTS.mobile_max;
  const isTabletSmall =
    width >= BREAKPOINTS.tablet_min && width <= BREAKPOINTS.tablet_small_max;
  const isTabletLarge =
    width >= BREAKPOINTS.tablet_large_min && width <= BREAKPOINTS.tablet_max;
  const isDesktopSmall =
    width >= BREAKPOINTS.desktop_min && width <= BREAKPOINTS.desktop_small_max;
  const isDesktopLarge = width >= BREAKPOINTS.desktop_large_min;

  return {
    isMobile,
    isTablet,
    isDesktop,
    isMobileSmall,
    isMobileLarge,
    isTabletSmall,
    isTabletLarge,
    isDesktopSmall,
    isDesktopLarge,
    width,
    height,
    isBreakpoint,
    isBetween,
    getSpacing: (size: GravitySpacing): number => SPACING[size],
    getContainer: (size: GravityContainer): number => CONTAINERS[size],
  };
}

export default useResponsive;
